import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format, subMonths } from "date-fns";
import { it } from "date-fns/locale";
import { Download, Send, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fmtDateTime } from "@/lib/api";

const L = ({ children }) => <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">{children}</div>;
const label = (m) => format(new Date(`${m}-01T12:00:00`), "MMMM yyyy", { locale: it });
const MONTHS = Array.from({ length: 13 }, (_, i) => format(subMonths(new Date(), i), "yyyy-MM"));

export const ReportDialog = ({ open, onOpenChange, client }) => {
  const [mese, setMese] = useState(format(subMonths(new Date(), 1), "yyyy-MM"));
  const [nota, setNota] = useState("");
  const [to, setTo] = useState("");
  const [oggetto, setOggetto] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");
  const [invii, setInvii] = useState([]);
  useEffect(() => { if (open) { setTo(client.email || ""); api.get(`/clients/${client.id}/report/invii`).then((r) => setInvii(r.data)); } }, [open, client]);
  useEffect(() => { setOggetto(`Report ${label(mese)} – ${client.ragione_sociale}`); setMsg(`In allegato trovi il report delle attività di ${label(mese)}.\n\nUn saluto`); }, [mese, client]);

  const download = async () => {
    setBusy("pdf");
    try {
      const r = await api.post(`/clients/${client.id}/report`, { mese, nota }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `Report_${mese}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Report scaricato");
    } catch (e) { toast.error("Generazione del report non riuscita"); }
    setBusy("");
  };
  const send = async () => {
    setBusy("send");
    try {
      const r = await api.post(`/clients/${client.id}/report/invia`, { mese, nota, to: to.split(/[,;\s]+/).filter(Boolean), oggetto, messaggio: msg });
      toast.success("Report inviato al cliente"); setInvii((p) => [r.data, ...p]);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl core-scroll" data-testid="report-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-black text-[#0A1128]">Report mensile</DialogTitle>
          <DialogDescription>PDF con post pubblicati, appuntamenti svolti e attività completate nel mese.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-end gap-3">
            <div><L>Mese</L>
              <Select value={mese} onValueChange={setMese}>
                <SelectTrigger className="rounded-xl w-56 capitalize" data-testid="report-month"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((m) => <SelectItem key={m} value={m} className="capitalize">{label(m)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={download} disabled={!!busy} className="rounded-full bg-[#0A1128] hover:bg-[#E8173A] font-bold" data-testid="report-download">{busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Download className="h-4 w-4 mr-1.5" />}Scarica PDF</Button>
          </div>
          <div><L>Nota da includere nel report (facoltativa)</L><Textarea rows={3} value={nota} onChange={(e) => setNota(e.target.value)} className="rounded-xl" data-testid="report-note" /></div>
          <div className="rounded-2xl bg-[#A6DAFC] p-4 space-y-3">
            <div className="font-bold text-[#0A1128]">Invia al cliente</div>
            <div><L>Destinatari</L><Input value={to} onChange={(e) => setTo(e.target.value)} className="rounded-xl" data-testid="report-to" /></div>
            <div><L>Oggetto</L><Input value={oggetto} onChange={(e) => setOggetto(e.target.value)} className="rounded-xl" data-testid="report-subject" /></div>
            <div><L>Messaggio</L><Textarea rows={4} value={msg} onChange={(e) => setMsg(e.target.value)} className="rounded-xl" data-testid="report-message" /></div>
            <Button onClick={send} disabled={!!busy} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="report-send">{busy === "send" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}Invia con PDF allegato</Button>
          </div>
          {invii.length > 0 && <div data-testid="report-history"><L>Storico invii</L>{invii.map((i) => <div key={i.id} className="text-xs text-slate-700">{fmtDateTime(i.sent_at)} · {label(i.mese)} · {i.to.join(", ")} · {i.status}{i.error ? ` · ${i.error}` : ""}</div>)}</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
